import classnames from 'classnames'
import ThemeConsumer from 'embed/components/ThemeConsumer'
import { css } from 'emotion'
import * as React from 'react'

const ChevronDown = require('./icons/chevron-down.svg')
const ChevronUp = require('./icons/chevron-up.svg')

const styles = require('./styles.scss')
export interface AccordionProps {
  children: JSX.Element[]
  collapsed: boolean
  maxHeight?: number
  minItemsShown?: number
  paddingRight?: number
  accordionPaddingLeft?: boolean
  toggleFullWidth?: boolean
  showMask?: boolean
}

export const MIN_ACCORDION_DISPLAY_ITEMS = 3

export const Accordion = ({
  children = [],
  collapsed,
  maxHeight,
  minItemsShown = MIN_ACCORDION_DISPLAY_ITEMS,
  paddingRight = 240,
  accordionPaddingLeft = true,
  toggleFullWidth = false,
  showMask = true,
}: AccordionProps) => {
  const elementRef = React.useRef<HTMLUListElement>(null)

  const [collapsedHeight, setCollapsedHeight] = React.useState<number | undefined>(undefined)
  const [displayHeight, setDisplayHeight] = React.useState<number | undefined>(undefined)
  const [isCollapsed, setIsCollapsed] = React.useState(collapsed)
  const [originalHeight, setOriginalHeight] = React.useState<number | undefined>(undefined)

  React.useEffect(() => {
    setTimeout(() => {
      let minimumHeight = 0
      const initialHeight = elementRef.current?.clientHeight

      if (minItemsShown > 0) {
        for (let i = 0; i < minItemsShown; i++) {
          minimumHeight += elementRef.current ? elementRef.current.children[i]?.clientHeight : 0
        }
      }

      setOriginalHeight(initialHeight)
      const updatedCollapsedHeight = maxHeight ?? minimumHeight
      setCollapsedHeight(updatedCollapsedHeight)
      setDisplayHeight(collapsed ? maxHeight ?? minimumHeight : initialHeight)
    }, 100)
  }, [elementRef])

  const toggleDetailsContainer = () => {
    const refHeight = elementRef.current?.clientHeight
    refHeight !== originalHeight ? setDisplayHeight(originalHeight) : setDisplayHeight(collapsedHeight)
    setIsCollapsed(!isCollapsed)
  }

  const ToggleButton = () => {
    const icon = isCollapsed ? <ChevronDown /> : <ChevronUp />
    const text = isCollapsed ? 'Expand Details' : 'Hide Details'

    return (
      <ThemeConsumer>
        {({ backdropStyles, fontSizeBase, fontFamilySecondary, colorPrimary }) => (
          <div
            className={classnames(
              styles.toggleButtonContainer,
              css`
                background-color: ${backdropStyles.backgroundColor};
                padding-right: ${paddingRight}px;
                margin: ${toggleFullWidth ? '0px -20px -20px -20px' : 'auto'};
              `,
            )}
            onClick={toggleDetailsContainer}
          >
            <span
              className={classnames(
                styles.toggleDetailsText,
                css`
                  color: ${colorPrimary};
                  font-family: ${fontFamilySecondary};
                  font-size: ${fontSizeBase};
                `,
              )}
              onClick={toggleDetailsContainer}
            >
              {text}
            </span>
            <span
              className={classnames(
                styles.iconContainer,
                css`
                  fill: ${colorPrimary};
                `,
              )}
            >
              {icon}
            </span>
          </div>
        )}
      </ThemeConsumer>
    )
  }

  const showToggle = originalHeight !== undefined && maxHeight !== undefined ? originalHeight > maxHeight : false

  return (
    <div data-test-id="accordion" className={classnames(styles.offerDetails)}>
      <ul
        data-test-id="details-list"
        className={classnames(
          styles.detailsList,
          {
            [styles.masked]: showMask && showToggle && isCollapsed,
          },
          css`
            height: ${displayHeight}px;
            padding-left: ${accordionPaddingLeft ? 'auto' : 0};
          `,
        )}
        ref={elementRef}
      >
        {children}
      </ul>
      {showToggle && <ToggleButton />}
    </div>
  )
}
